import {
  CSSProperties,
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

export const PRIMIS_OUTSTREAM_CONTAINER_ID = 'primis-player-container';
export const PRIMIS_PLAYER_SCRIPT_SRC = 'https://live.primis.tech/live/liveView.php?s=122375';
export const PRIMIS_FLOATING_FALLBACK_SCRIPT_SRC = `${PRIMIS_PLAYER_SCRIPT_SRC}&videoType=sticky`;
export const PRIMIS_DOCKED_PLAYER_SCRIPT_SRC = 'https://live.primis.tech/live/liveView.php?s=122580&videoType=flow&flowMode=both';
export const PRIMIS_VENDOR_PLAYER_CONTAINER_ID = 'primisPlayerContainerDiv';

const primisOutstreamContainerStyle: CSSProperties = {
  height: 0,
  minHeight: 0,
  lineHeight: 0,
  fontSize: 0,
  overflow: 'visible'
};

const usePrimisRegistrationEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const primisPlayerArtifactSelector = [
  `#${PRIMIS_VENDOR_PLAYER_CONTAINER_ID}`,
  '[id^="primis_container_div_"]',
  '[id^="primis_playerSekindoSPlayer"]',
  '[id^="Player-Div-SekindoSPlayer"]',
  '[id^="Video-Div-SekindoSPlayer"]',
  '[id^="Video-iFrame-SekindoSPlayer"]',
  '#contextMenuPrimis',
  '#contextMenusPrimisLogo',
  '#primisLogoWrapper',
  '#primisLogo',
  '#primisLogoTxt',
  '#primisVastIconsContainer',
  'script[src*="live.primis.tech/live/liveView.php"]'
].join(',');

type TPrimisPlacementContext = {
  dockedPlacementCount: number;
  registerDockedPlacement: () => { unregister: () => void };
};

const PrimisPlacementContext = createContext<TPrimisPlacementContext>({
  dockedPlacementCount: 0,
  registerDockedPlacement: () => ({
    unregister: () => {
      /* nop */
    }
  })
});

function createPrimisScript(src: string) {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = src;
  script.setAttribute('language', 'javascript');
  return script;
}

function removePrimisPlayerArtifactsOutsideDockedSlots() {
  document.querySelectorAll(primisPlayerArtifactSelector).forEach((element) => {
    if (element.closest('[data-primis-docked-slot="true"]')) {
      return;
    }

    element.remove();
  });
}

type TPrimisPlayerTagProps = {
  id?: string;
  scriptSrc: string;
  style?: CSSProperties;
};

const PrimisPlayerTag: FC<TPrimisPlayerTagProps> = ({ id, scriptSrc, style }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const script = createPrimisScript(scriptSrc);
    container.appendChild(script);

    return () => {
      script.remove();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      removePrimisPlayerArtifactsOutsideDockedSlots();
    };
  }, [scriptSrc]);

  return <div id={id} ref={containerRef} style={style} />;
};

export const PrimisPlacementProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dockedPlacementCount, setDockedPlacementCount] = useState(0);
  const registerDockedPlacement = useCallback(() => {
    let isRegistered = true;
    setDockedPlacementCount((count) => count + 1);

    return {
      unregister: () => {
        if (!isRegistered) {
          return;
        }

        isRegistered = false;
        setDockedPlacementCount((count) => count - 1);
      }
    };
  }, []);
  const value = useMemo(
    () => ({
      dockedPlacementCount,
      registerDockedPlacement
    }),
    [dockedPlacementCount, registerDockedPlacement]
  );

  return <PrimisPlacementContext.Provider value={value}>{children}</PrimisPlacementContext.Provider>;
};

export function usePrimisDockedPlacementRegistration(enabled: boolean) {
  const { registerDockedPlacement } = useContext(PrimisPlacementContext);

  usePrimisRegistrationEffect(() => {
    if (!enabled) {
      return;
    }

    const registration = registerDockedPlacement();

    return () => {
      registration.unregister();
    };
  }, [enabled, registerDockedPlacement]);
}

export function buildPrimisDockedPlayerScriptSrc(width: number, height: number) {
  return `${PRIMIS_DOCKED_PLAYER_SCRIPT_SRC}&x=${width}&y=${height}`;
}

export const PrimisDockedPlayer: FC<{ width: number; height: number }> = ({ width, height }) => (
  <PrimisPlayerTag scriptSrc={buildPrimisDockedPlayerScriptSrc(width, height)} />
);

export const PrimisFloatingFallback: FC = () => {
  const { dockedPlacementCount } = useContext(PrimisPlacementContext);
  const [canMountFallback, setCanMountFallback] = useState(false);

  useEffect(() => {
    const mountTimeout = window.setTimeout(() => {
      setCanMountFallback(true);
    }, 0);

    return () => {
      window.clearTimeout(mountTimeout);
    };
  }, []);

  if (!canMountFallback || dockedPlacementCount > 0) {
    return null;
  }

  return (
    <PrimisPlayerTag
      id={PRIMIS_OUTSTREAM_CONTAINER_ID}
      scriptSrc={PRIMIS_FLOATING_FALLBACK_SCRIPT_SRC}
      style={primisOutstreamContainerStyle}
    />
  );
};

export const PrimisOutstream: FC = () => (
  <PrimisPlayerTag
    id={PRIMIS_OUTSTREAM_CONTAINER_ID}
    scriptSrc={PRIMIS_PLAYER_SCRIPT_SRC}
    style={primisOutstreamContainerStyle}
  />
);
